import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useConsentTracking } from '@/hooks/useConsentTracking';
import { PURCHASE_CONSENT_VERSION } from '@/lib/constants/documentVersions';
import { getUserLanguage } from '@/lib/i18n/languageDetection';
import { toast } from 'sonner';

interface PurchaseConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: {
    name: string;
    price: string;
    reviews: number;
    packType: string;
  };
  userId: string;
  onConfirm: () => void;
  isLoading?: boolean;
  /**
   * 'subscription' renders the recurring-price summary and the
   * subscription-specific pay button required by Sections 312j(2)/(3) BGB.
   */
  mode?: 'one_off' | 'subscription';
}

// Localized text for EU consumer-law clarity (BGB § 312j is German-specific,
// but FR/ES users buying through Paddle deserve native-language withdrawal-waiver wording).
const translations = {
  en: {
    title: 'Complete Your Purchase',
    description: 'Please review and confirm the following before proceeding.',
    withdrawalLabel: 'I expressly consent to the immediate performance of the contract and acknowledge that I',
    withdrawalBold: 'lose my right of withdrawal',
    withdrawalEnd: 'once the digital content (AI review credits) is provided.',
    termsLabel: 'I have read and accept the',
    termsOfService: 'Terms of Service',
    cancellationPolicy: 'Cancellation Policy',
    refundPolicy: 'Refund Policy',
    processing: 'Processing...',
    submitButton: 'Order with obligation to pay',
    submitNote: 'By clicking above, you agree to pay for this purchase.',
    vatNote: '(incl. VAT where applicable)',
    aiReviews: 'AI reviews',
    consentError: 'Unable to record your consent. Please try again.',
    conjunction: ', and ',
    suffix: '.',
    recurringTitle: 'Recurring subscription',
    recurringLine: 'Billed monthly. The subscription renews automatically at the same price until you cancel; you can cancel at any time with effect from the end of the current billing period. Minimum term: one month.',
    creditsLabel: 'credits per month',
    submitButtonSubscription: 'Subscribe with obligation to pay',
    submitNoteSubscription: 'By clicking above, you enter into a paid subscription that renews monthly until cancelled.',
    withdrawalEndSubscription: 'once the credits for the current billing period are made available.',
  },
  de: {
    title: 'Kauf abschließen',
    description: 'Bitte prüfen und bestätigen Sie die folgenden Punkte.',
    withdrawalLabel: 'Ich stimme ausdrücklich zu, dass mit der Vertragserfüllung sofort begonnen wird, und erkenne an, dass ich',
    withdrawalBold: 'mein Widerrufsrecht verliere',
    withdrawalEnd: ', sobald die digitalen Inhalte (KI-Review-Guthaben) bereitgestellt werden.',
    termsLabel: 'Ich habe die',
    termsOfService: 'Nutzungsbedingungen',
    cancellationPolicy: 'Widerrufsbelehrung',
    refundPolicy: 'Erstattungsrichtlinie',
    processing: 'Wird verarbeitet...',
    submitButton: 'Zahlungspflichtig bestellen',
    submitNote: 'Mit Klick auf die Schaltfläche bestätigen Sie Ihre Zahlungsverpflichtung.',
    vatNote: '(inkl. MwSt.)',
    aiReviews: 'KI-Reviews',
    consentError: 'Ihre Einwilligung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
    conjunction: ' und ',
    suffix: ' gelesen und akzeptiere diese.',
    recurringTitle: 'Abonnement mit wiederkehrender Zahlung',
    recurringLine: 'Abrechnung monatlich. Das Abonnement verlängert sich automatisch zum gleichen Preis, bis Sie kündigen; eine Kündigung ist jederzeit zum Ende der laufenden Abrechnungsperiode möglich. Mindestlaufzeit: ein Monat.',
    creditsLabel: 'Credits pro Monat',
    submitButtonSubscription: 'Zahlungspflichtig abonnieren',
    submitNoteSubscription: 'Mit Klick auf die Schaltfläche schließen Sie ein kostenpflichtiges Abonnement ab, das sich monatlich verlängert, bis Sie kündigen.',
    withdrawalEndSubscription: ', sobald die Credits der laufenden Abrechnungsperiode bereitgestellt werden.',
  },
  fr: {
    title: "Finaliser l'achat",
    description: 'Veuillez vérifier et confirmer les points suivants avant de continuer.',
    withdrawalLabel: "Je consens expressément à l'exécution immédiate du contrat et reconnais que je",
    withdrawalBold: 'perds mon droit de rétractation',
    withdrawalEnd: " dès que le contenu numérique (crédits d'analyse IA) est fourni.",
    termsLabel: "J'ai lu et j'accepte les",
    termsOfService: "Conditions d'utilisation",
    cancellationPolicy: 'Politique de rétractation',
    refundPolicy: 'Politique de remboursement',
    processing: 'Traitement en cours...',
    submitButton: 'Acheter avec obligation de paiement',
    submitNote: "En cliquant ci-dessus, vous acceptez de payer pour cet achat.",
    vatNote: '(TVA incluse le cas échéant)',
    aiReviews: 'analyses IA',
    consentError: "Impossible d'enregistrer votre consentement. Veuillez réessayer.",
    conjunction: ' et ',
    suffix: '.',
    recurringTitle: 'Abonnement récurrent',
    recurringLine: 'Facturation mensuelle. L\'abonnement se renouvelle automatiquement au même prix jusqu\'à résiliation ; vous pouvez résilier à tout moment avec effet à la fin de la période de facturation en cours. Durée minimale : un mois.',
    creditsLabel: 'crédits par mois',
    submitButtonSubscription: 'S\'abonner avec obligation de paiement',
    submitNoteSubscription: 'En cliquant ci-dessus, vous souscrivez un abonnement payant renouvelé mensuellement jusqu\'à résiliation.',
    withdrawalEndSubscription: ' dès que les crédits de la période de facturation en cours sont mis à disposition.',
  },
  es: {
    title: 'Completar la compra',
    description: 'Revise y confirme los siguientes puntos antes de continuar.',
    withdrawalLabel: 'Doy mi consentimiento expreso a la ejecución inmediata del contrato y reconozco que',
    withdrawalBold: 'pierdo mi derecho de desistimiento',
    withdrawalEnd: ' una vez proporcionado el contenido digital (créditos de análisis IA).',
    termsLabel: 'He leído y acepto las',
    termsOfService: 'Condiciones de uso',
    cancellationPolicy: 'Política de desistimiento',
    refundPolicy: 'Política de reembolso',
    processing: 'Procesando...',
    submitButton: 'Comprar con obligación de pago',
    submitNote: 'Al hacer clic arriba, acepta pagar esta compra.',
    vatNote: '(IVA incluido cuando corresponda)',
    aiReviews: 'análisis IA',
    consentError: 'No se pudo registrar su consentimiento. Inténtelo de nuevo.',
    conjunction: ' y ',
    suffix: '.',
    recurringTitle: 'Suscripción recurrente',
    recurringLine: 'Facturación mensual. La suscripción se renueva automáticamente al mismo precio hasta que la cancele; puede cancelar en cualquier momento con efectos al final del período de facturación en curso. Duración mínima: un mes.',
    creditsLabel: 'créditos al mes',
    submitButtonSubscription: 'Suscribirse con obligación de pago',
    submitNoteSubscription: 'Al hacer clic arriba, contrata una suscripción de pago que se renueva mensualmente hasta su cancelación.',
    withdrawalEndSubscription: ' una vez puestos a disposición los créditos del período de facturación en curso.',
  },
};

export const PurchaseConsentDialog = ({
  open,
  onOpenChange,
  tier,
  userId,
  onConfirm,
  isLoading = false,
  mode = 'one_off',
}: PurchaseConsentDialogProps) => {
  const [withdrawalConsent, setWithdrawalConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { logConsent, getClientInfo } = useConsentTracking();

  // Get user's language preference for localized text
  const language = getUserLanguage();

  // Pick locale-appropriate translation; default to English for unknown languages.
  const t = translations[language as keyof typeof translations] ?? translations.en;

  const isSubscription = mode === 'subscription';

  const canProceed = withdrawalConsent && termsConsent && !isLoading && !isSubmitting;

  const handleConfirm = async () => {
    if (!canProceed) return;

    setIsSubmitting(true);
    const clientInfo = getClientInfo();
    let consentLogged = false;

    // Log both consents for legal proof (Art. 7(1) DSGVO)
    // CRITICAL: Block checkout if consent cannot be recorded
    try {
      const results = await Promise.all([
        logConsent(
          {
            consentType: 'digital_content_withdrawal_waiver',
            documentVersion: PURCHASE_CONSENT_VERSION,
            userAgent: clientInfo.userAgent,
          },
          userId
        ),
        logConsent(
          {
            consentType: 'purchase_terms_acceptance',
            documentVersion: PURCHASE_CONSENT_VERSION,
            userAgent: clientInfo.userAgent,
          },
          userId
        ),
      ]);

      // Check if at least one consent was logged successfully
      consentLogged = results.some((r) => r === true);
    } catch (error) {
      console.error('Consent logging failed:', error);
      consentLogged = false;
    }

    if (!consentLogged) {
      toast.error(t.consentError);
      setIsSubmitting(false);
      return; // Block checkout - consent proof is legally required
    }

    setIsSubmitting(false);
    onConfirm();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset checkboxes when dialog closes
      setWithdrawalConsent(false);
      setTermsConsent(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {t.title}
          </DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Product summary */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-1">
            <p className="font-medium">{tier.name}</p>
            <p className="text-sm text-muted-foreground">
              {tier.reviews} {isSubscription ? t.creditsLabel : t.aiReviews}
            </p>
            <p className="text-lg font-bold">
              {tier.price}{' '}
              <span className="text-xs font-normal text-muted-foreground">
                {t.vatNote}
              </span>
            </p>
          </div>

          {/* Recurring price disclosure - Section 312j(2) BGB */}
          {isSubscription && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-1">
              <p className="text-sm font-medium">{t.recurringTitle}</p>
              <p className="text-xs text-muted-foreground">{t.recurringLine}</p>
            </div>
          )}

          {/* Consent checkboxes */}
          <div className="space-y-4">
            {/* Withdrawal waiver - Section 356(5) BGB */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="withdrawal-consent"
                checked={withdrawalConsent}
                onCheckedChange={(checked) =>
                  setWithdrawalConsent(checked === true)
                }
                className="mt-1"
              />
              <Label
                htmlFor="withdrawal-consent"
                className="text-sm leading-relaxed cursor-pointer"
              >
                {t.withdrawalLabel}{' '}
                <strong>{t.withdrawalBold}</strong>
                {isSubscription ? t.withdrawalEndSubscription : t.withdrawalEnd}
              </Label>
            </div>

            {/* Terms, Cancellation Policy, and Refund Policy acceptance */}
            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms-consent"
                checked={termsConsent}
                onCheckedChange={(checked) => setTermsConsent(checked === true)}
                className="mt-1"
              />
              <Label
                htmlFor="terms-consent"
                className="text-sm leading-relaxed cursor-pointer"
              >
                {t.termsLabel}{' '}
                <Link
                  to="/terms-of-service"
                  target="_blank"
                  className="text-primary underline hover:no-underline"
                >
                  {t.termsOfService}
                </Link>
                ,{' '}
                <Link
                  to="/cancellation-policy"
                  target="_blank"
                  className="text-primary underline hover:no-underline"
                >
                  {t.cancellationPolicy}
                </Link>
                {language === 'de' ? ' und ' : ', and '}
                <Link
                  to="/refund-policy"
                  target="_blank"
                  className="text-primary underline hover:no-underline"
                >
                  {t.refundPolicy}
                </Link>
                {language === 'de' ? ' gelesen und akzeptiere diese.' : '.'}
              </Label>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {/* German law compliant button text - Section 312j(3) BGB */}
          <Button
            onClick={handleConfirm}
            disabled={!canProceed}
            className="w-full"
            size="lg"
          >
            {isLoading || isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.processing}
              </>
            ) : (
              isSubscription ? t.submitButtonSubscription : t.submitButton
            )}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            {isSubscription ? t.submitNoteSubscription : t.submitNote}
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
